import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import QrScanner from "./../QrScanner";
import AddProductManually from "../AddProductManually";
import {
  CloseRounded,
  KeyboardRounded,
  QrCodeRounded,
} from "@mui/icons-material";
import { AddProduct } from "../../../../api/inventory";
import { useInventory } from "../../InventoryProvider";
import {
  InventoryData,
  Product,
  Status,
} from "../../../../types/inventoryData";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { useTranslation } from "react-i18next";
import { ErrorCodes } from "../../../../types/enums";

type AddItemPopupTypes = {
  open: boolean;
  handleClose: () => void;
  handleOpenBatchEdit: () => void;
};

type PanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const CustomTabPanel: React.FC<PanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel${index}`}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      {value === index && children}
    </div>
  );
};

const AddProductPopup: React.FC<AddItemPopupTypes> = ({
  open,
  handleClose,
  handleOpenBatchEdit,
}) => {
  const [value, setValue] = useState(0);
  const {
    setRows,
    setSelectedProductIds,
    setFilteredRows,
    isPhoneSize,
  } = useInventory();
  const [listOfScannedQr, setListOfScannedQr] = useState<string[]>([]);
  const [listOfAddedProductIds, setListOfAddedProductIds] = useState<number[]>(
    []
  );
  const { addBatchNumberAndActions } = useInventory();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleAdd = async (
    qrValue: string,
    status?: Status | undefined,
    note?: string | null | undefined,
    location?: string | null | undefined,
    reset?: any
  ) => {
    qrValue.replaceAll(" ", "");

    try {
      let product: Product = {
        serial: qrValue,
        status: status,
        note: note,
        location: location,
      };
      const newProduct: InventoryData = await AddProduct(product);
      const finalProduct = addBatchNumberAndActions(newProduct);

      setListOfScannedQr((prev) => {
        const alreadyScanned = prev.includes(qrValue);
        if (alreadyScanned) {
          showSnackbar(t("inventory.productAlreadyAdded"), "info");
          return prev;
        }
        const updatedListOfScannedQr = [...prev, qrValue];
        return updatedListOfScannedQr;
      });

      setFilteredRows((prev) => [finalProduct, ...prev]);
      setRows((prev) => [finalProduct, ...prev]);

      setListOfAddedProductIds((prev) => {
        const alreadyAdded = prev.some(
          (rowId) => rowId === newProduct.productId
        );
        if (alreadyAdded) return prev;
        return [...prev, newProduct.productId];
      });
      showSnackbar(t("inventory.productAddedSuccessfully"), "success");
      if(reset) reset();
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.ProductAlreadyExist:
            showSnackbar(t("errors.ProductAlreadyExist"), "error");
            break;
          case ErrorCodes.MissingSerialField:
            showSnackbar(t("errors.MissingSerialField"), "error");
            break;
          case ErrorCodes.ProductTypeNotFound:
            showSnackbar(t("errors.ProductTypeNotFound"), "error");
            break;
          default:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
        }
      } else {
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhoneSize}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            // width: 600,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              px: 3,
              py: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <DialogTitle sx={{ p: 0 }}>{t("inventory.addNewProduct")}</DialogTitle>
              <IconButton
                onClick={handleClose}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { cursor: "pointer" },
                }}
              >
                <CloseRounded />
              </IconButton>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>{listOfScannedQr.length} {t("inventory.productsAdded")}</Typography>
              <Button
                variant="contained"
                onClick={() => {
                  handleOpenBatchEdit();
                  setSelectedProductIds(listOfAddedProductIds);
                }}
                disabled={listOfScannedQr.length < 1}
              >
                {t("inventory.editProducts")}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              width: "100%",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              selectionFollowsFocus
              variant="fullWidth"
            >
              <Tab icon={<QrCodeRounded />} label="Scan" />
              <Tab icon={<KeyboardRounded />} label="Manual" />
            </Tabs>
          </Box>

          <CustomTabPanel value={value} index={0}>
            <QrScanner handleScan={(res) => handleAdd(res[0].rawValue)} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <AddProductManually handleAdd={handleAdd} />
          </CustomTabPanel>
        </Box>
      </Dialog>
    </>
  );
};

export default AddProductPopup;
